import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Plus, Printer } from "lucide-react";

import { HubUserAvatar } from "@/components/HubUserAvatar";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { TextChipInput, type TextChipInputHandle } from "@/components/ui/TextChipInput";
import { CardTitleWithHelp, PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { SalesOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import { DetailLinkWithEntityPeek } from "@/components/hub/DetailLinkWithEntityPeek";
import { ApiError } from "@/lib/api";
import { buildSalesOrderApprovalTicketPath } from "@/lib/hubEntityDetailPaths";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { qcIncidentPath } from "@/lib/qc/qcIncidentNav";
import { ManufacturingLocationChip } from "@/lib/manufacturingLocationChip";
import { EM_DASH } from "@/lib/utils";

import { QualityApprovalPicker } from "./QualityApprovalPicker";
import { QualityCustomerPicker } from "./QualityCustomerPicker";
import { QualityFreshdeskTicketPicker } from "./QualityFreshdeskTicketPicker";
import { QualityRmaIntakeModal } from "./QualityRmaIntakeModal";
import { QualityRmaLinkIdModal } from "./QualityRmaLinkIdModal";
import { QualityRmaUpsLabelQtyModal } from "./QualityRmaUpsLabelQtyModal";
import { QualityRmaQuantityLines } from "./QualityRmaQuantityLines";
import { QualitySalesOrderPicker } from "./QualitySalesOrderPicker";
import {
  QualityRmaAddressFields,
  rmaAddressCountry,
  type QualityRmaAddressField,
} from "./QualityRmaAddressFields";
import { QualityRmaSavedAddressPicker } from "./QualityRmaSavedAddressPicker";
import {
  QUALITY_RMAS_LIST_KEY,
  RMA_STATUSES,
  addRmaApproval,
  addRmaDevice,
  addRmaFreshdeskTicket,
  addRmaSalesOrder,
  createRmaUpsReturnLabel,
  downloadRmaDocument,
  fetchQualityRmaDetail,
  linkRmaInvestigation,
  linkRmaRca,
  openRmaRca,
  qualityRmaQueryKey,
  removeRmaApproval,
  removeRmaDevice,
  removeRmaFreshdeskTicket,
  removeRmaSalesOrder,
  rmaLedByLabel,
  rmaStatusLabel,
  startRmaReplacementApproval,
  updateQualityRma,
  type CreateQualityRmaInput,
  type QualityRmaDetailResponse,
  type RmaStatus,
} from "./rmaApi";
import { displayRmaQuantityModelLines, formatRmaQuantityModelLines, type RmaQuantityModelLine } from "./rmaQuantity";

const HELP_TEXT = [
  "An RMA is the transit document for a return. Received is the last active RMA status. Open an RCA from here when the returned devices need root cause analysis.",
  "Link a quality investigation when you need a formal case writeup.",
].join(" ");

const SHIP_TO_FIELD_KEYS: Record<QualityRmaAddressField, keyof CreateQualityRmaInput> = {
  line1: "shipToLine1",
  line2: "shipToLine2",
  city: "shipToCity",
  state: "shipToState",
  postalCode: "shipToPostalCode",
};

const SHIP_FROM_FIELD_KEYS: Record<QualityRmaAddressField, keyof CreateQualityRmaInput> = {
  line1: "shipFromLine1",
  line2: "shipFromLine2",
  city: "shipFromCity",
  state: "shipFromState",
  postalCode: "shipFromPostalCode",
};

function rmaSerialKey(value: string | null | undefined): string {
  return (value ?? "").trim().toUpperCase();
}

function RmaDeviceSerialMarks({
  device,
}: {
  device: QualityRmaDetailResponse["devices"][number] | undefined;
}): JSX.Element | null {
  if (device == null) return null;
  return (
    <span className="mt-1 flex flex-wrap items-center gap-2">
      {device.firmwareVersion ? <span className="text-slate-500">FW {device.firmwareVersion}</span> : null}
      {device.communicating ? (
        <span className="rounded bg-sky-100 px-1.5 py-0.5 text-xs text-sky-900">Communicating</span>
      ) : null}
      {device.qualityIncidentId != null ? (
        <Link className="text-sky-800 hover:underline" to={qcIncidentPath(device.qualityIncidentId, "quality")}>
          QC incident {device.qualityIncidentId}
          {device.qualityIncidentOpen ? " (open)" : ""}
          {device.qualityIncidentRepairScrap ? ` · ${device.qualityIncidentRepairScrap}` : ""}
        </Link>
      ) : null}
    </span>
  );
}

function RmaSerialLookupLink({
  serial,
  model,
  firmwareVersion,
  pcbaLocationId,
  communicating,
}: {
  serial: string;
  model?: string | null;
  firmwareVersion?: string | null;
  pcbaLocationId?: number | null;
  communicating?: boolean;
}): JSX.Element {
  return (
    <DetailLinkWithEntityPeek
      peek={{
        kind: "device",
        context: {
          serial,
          model,
          firmwareVersion,
          pcbaLocationId,
          communicating,
        },
      }}
      trigger={
        <Link
          className="text-sky-800 hover:underline"
          to={`/quality/devices?label=${encodeURIComponent(serial)}`}
        >
          {serial}
        </Link>
      }
    />
  );
}

function PcbaManufacturerCell({ locationId }: { locationId: number | null | undefined }): JSX.Element {
  if (locationId == null || locationId <= 0) return <>{EM_DASH}</>;
  return <ManufacturingLocationChip locationId={locationId} />;
}

export function QualityRmaDetailPage() {
  const params = useParams<{ id: string }>();
  const rmaId = Number(params.id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const writesAllowed = useHubModuleWrite("quality");
  const [deviceDraftLabels, setDeviceDraftLabels] = useState<string[]>([]);
  const deviceChipRef = useRef<TextChipInputHandle>(null);
  const [intakeModalOpen, setIntakeModalOpen] = useState(false);
  const [intakeEditing, setIntakeEditing] = useState<QualityRmaDetailResponse["intake"][number] | null>(null);
  const [intakeSeed, setIntakeSeed] = useState<{ unitSerial: string; unitModel: string } | null>(null);
  const [preIntakeOpen, setPreIntakeOpen] = useState(false);
  const [upsLabelQtyOpen, setUpsLabelQtyOpen] = useState(false);
  const [linkRcaOpen, setLinkRcaOpen] = useState(false);
  const [openRcaPending, setOpenRcaPending] = useState(false);
  const [linkInvestigationOpen, setLinkInvestigationOpen] = useState(false);
  const [salesOrderDraft, setSalesOrderDraft] = useState<number | null>(null);
  const [approvalDraft, setApprovalDraft] = useState<number | null>(null);
  const [skuLines, setSkuLines] = useState<RmaQuantityModelLine[]>([{ quantity: "", models: "", reason: "" }]);

  const detailQuery = useQuery({
    queryKey: qualityRmaQueryKey(rmaId),
    queryFn: () => fetchQualityRmaDetail(rmaId),
    enabled: Number.isFinite(rmaId) && rmaId > 0,
  });

  useEffect(() => {
    setSkuLines(displayRmaQuantityModelLines(detailQuery.data?.rma.quantityAndModels));
  }, [detailQuery.data?.rma.quantityAndModels]);

  const invalidate = async (detail: Awaited<ReturnType<typeof fetchQualityRmaDetail>>) => {
    queryClient.setQueryData(qualityRmaQueryKey(rmaId), detail);
    await queryClient.invalidateQueries({ queryKey: QUALITY_RMAS_LIST_KEY });
  };

  const saveMutation = useMutation({
    mutationFn: (input: Parameters<typeof updateQualityRma>[1]) => updateQualityRma(rmaId, input),
    onSuccess: async (detail) => {
      await invalidate(detail);
      showToast({ tone: "success", message: "Saved RMA." });
    },
    onError: (error: unknown) => {
      showToast({ tone: "error", message: error instanceof ApiError ? error.message : "Could not save the RMA." });
    },
  });

  const startApprovalMutation = useMutation({
    mutationFn: () => startRmaReplacementApproval(rmaId),
    onSuccess: async (detail) => {
      await invalidate(detail);
      showToast({
        tone: "success",
        message: "Started a replacement approval and linked it to this RMA.",
      });
    },
    onError: (error: unknown) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "Could not start a replacement approval.",
      });
    },
  });

  if (!Number.isFinite(rmaId) || rmaId < 1) {
    return <p className="text-sm text-red-700">Invalid RMA id.</p>;
  }
  if (detailQuery.isLoading) {
    return <p className="text-sm text-slate-600">Loading RMA…</p>;
  }
  if (detailQuery.isError || detailQuery.data == null) {
    return <p className="text-sm text-red-700">{(detailQuery.error as Error)?.message ?? "RMA was not found."}</p>;
  }

  const detail = detailQuery.data;
  const rma = detail.rma;
  const devicesBySerial = new Map<string, QualityRmaDetailResponse["devices"][number]>();
  for (const device of detail.devices) {
    const key = rmaSerialKey(device.deviceLabelSerial);
    if (key !== "") devicesBySerial.set(key, device);
  }
  const intakeSerials = new Set(
    detail.intake.map((row) => rmaSerialKey(row.unitSerial)).filter((key) => key !== ""),
  );
  const preIntakeDevices = detail.devices.filter(
    (device) => !intakeSerials.has(rmaSerialKey(device.deviceLabelSerial)),
  );

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <PageTitleWithHelp hoverHint={HELP_TEXT} breadcrumbLabel={rma.rmaNumber}>
            {rma.rmaNumber}
          </PageTitleWithHelp>
          <div className="flex flex-wrap gap-2">
            <Badge>{rmaStatusLabel(rma.status)}</Badge>
            <Badge tone="neutral">{rmaLedByLabel(rma.ledBy)}</Badge>
            {rma.receiveQuantityDiscrepancy ? (
              <Badge tone="warning">Quantity discrepancy</Badge>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => void downloadRmaDocument(rma.id, rma.rmaNumber, "customer")}
          >
            <Download className="mr-2 h-4 w-4" />
            Customer RMA form
          </Button>
          <Button
            variant="secondary"
            onClick={() => void downloadRmaDocument(rma.id, rma.rmaNumber, "internal")}
          >
            Internal log
          </Button>
          <Button variant="secondary" onClick={() => void navigate(`/quality/rmas/${rma.id}/document`)}>
            <Printer className="mr-2 h-4 w-4" />
            Print / PDF
          </Button>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Header</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-2">
          <div>
            <Label htmlFor="rma-status">Status</Label>
            <select
              id="rma-status"
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              value={rma.status}
              disabled={!writesAllowed}
              onChange={(event) => saveMutation.mutate({ status: event.target.value as RmaStatus })}
            >
              {RMA_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {rmaStatusLabel(status)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Led by</Label>
            <SegmentedControl
              aria-label="Led by"
              className="mt-1 h-10 w-full"
              value={rma.ledBy}
              disabled={!writesAllowed}
              onChange={(next) => saveMutation.mutate({ ledBy: next })}
              options={[
                { value: "customer", label: rmaLedByLabel("customer") },
                { value: "end_user", label: rmaLedByLabel("end_user") },
              ]}
            />
          </div>
          <div>
            <Label>Customer</Label>
            <QualityCustomerPicker
              id="rma-customer"
              value={rma.customerFsAccountId}
              knownLabel={rma.customerName}
              disabled={!writesAllowed}
              onChange={(customerFsAccountId) => saveMutation.mutate({ customerFsAccountId })}
            />
          </div>
          <div>
            <Label>End user</Label>
            <QualityCustomerPicker
              id="rma-end-user"
              value={rma.endUserFsAccountId}
              knownLabel={rma.endUserName}
              disabled={!writesAllowed}
              onChange={(endUserFsAccountId) => saveMutation.mutate({ endUserFsAccountId })}
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="rma-complaint">Complaint</Label>
            <Input
              id="rma-complaint"
              defaultValue={rma.complaint ?? ""}
              disabled={!writesAllowed}
              onBlur={(event) => {
                const next = event.target.value.trim() || null;
                if (next !== (rma.complaint ?? null)) {
                  saveMutation.mutate({ complaint: next });
                }
              }}
            />
          </div>
          <div className="md:col-span-2 space-y-2">
            <Label>Freshdesk tickets (optional)</Label>
            <QualityFreshdeskTicketPicker
              id="rma-freshdesk"
              values={detail.freshdeskTicketIds}
              disabled={!writesAllowed}
              onChange={(ticketIds) => {
                if (!writesAllowed) return;
                const current = new Set(detail.freshdeskTicketIds);
                const next = new Set(ticketIds);
                const added = ticketIds.filter((ticketId) => !current.has(ticketId));
                const removed = detail.freshdeskTicketIds.filter((ticketId) => !next.has(ticketId));
                void (async () => {
                  try {
                    let latest = detail;
                    for (const ticketId of added) {
                      latest = await addRmaFreshdeskTicket(rma.id, ticketId);
                    }
                    for (const ticketId of removed) {
                      latest = await removeRmaFreshdeskTicket(rma.id, ticketId);
                    }
                    await invalidate(latest);
                  } catch (error) {
                    showToast({
                      tone: "error",
                      message: error instanceof ApiError ? error.message : "Could not update Freshdesk tickets.",
                    });
                  }
                })();
              }}
            />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Customer packing form</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-2">
          <p className="md:col-span-2 text-sm text-slate-600">
            These fields print on the official RMA form (put in the box, RMA on the carton). Internal intake stays
            separate. Return ship-from is also used for UPS return labels.
          </p>
          <div>
            <Label htmlFor="rma-contact-name">Contact name</Label>
            <Input
              id="rma-contact-name"
              defaultValue={rma.contactName ?? ""}
              disabled={!writesAllowed}
              onBlur={(event) => {
                const next = event.target.value.trim() || null;
                if (next !== (rma.contactName ?? null)) saveMutation.mutate({ contactName: next });
              }}
            />
          </div>
          <div>
            <Label htmlFor="rma-contact-email">Contact email</Label>
            <Input
              id="rma-contact-email"
              defaultValue={rma.contactEmail ?? ""}
              disabled={!writesAllowed}
              onBlur={(event) => {
                const next = event.target.value.trim() || null;
                if (next !== (rma.contactEmail ?? null)) saveMutation.mutate({ contactEmail: next });
              }}
            />
          </div>
          <p className="md:col-span-2 text-xs font-medium text-slate-600">Return ship-from</p>
          <QualityRmaSavedAddressPicker
            id="rma-ship-from-saved"
            customerFsAccountId={rma.customerFsAccountId}
            endUserFsAccountId={rma.endUserFsAccountId}
            disabled={!writesAllowed}
            onApply={(next) => {
              saveMutation.mutate({
                shipFromLine1: next.line1.trim() || null,
                shipFromLine2: next.line2.trim() || null,
                shipFromCity: next.city.trim() || null,
                shipFromState: next.state.trim() || null,
                shipFromPostalCode: next.postalCode.trim() || null,
                shipFromCountry: rmaAddressCountry(next),
              });
            }}
          />
          <QualityRmaAddressFields
            key={`ship-from-${rma.updatedAt}`}
            idPrefix="rma-ship-from"
            values={{
              line1: rma.shipFromLine1 ?? "",
              line2: rma.shipFromLine2 ?? "",
              city: rma.shipFromCity ?? "",
              state: rma.shipFromState ?? "",
              postalCode: rma.shipFromPostalCode ?? "",
            }}
            disabled={!writesAllowed}
            onFieldCommit={(field, next) => {
              const key = SHIP_FROM_FIELD_KEYS[field];
              const current = rma[key as "shipFromLine1"] ?? null;
              if (next === current) return;
              saveMutation.mutate(
                next != null ? { [key]: next, shipFromCountry: rma.shipFromCountry ?? "US" } : { [key]: next },
              );
            }}
          />
          <div className="md:col-span-2">
            <Label htmlFor="rma-purchase-ref">Order date / purchase reference (optional)</Label>
            <Input
              id="rma-purchase-ref"
              defaultValue={rma.purchaseReference ?? ""}
              disabled={!writesAllowed}
              onBlur={(event) => {
                const next = event.target.value.trim() || null;
                if (next !== (rma.purchaseReference ?? null)) saveMutation.mutate({ purchaseReference: next });
              }}
            />
          </div>
          <QualityRmaQuantityLines
            idPrefix="rma"
            lines={skuLines}
            onChange={setSkuLines}
            disabled={!writesAllowed}
            onCommit={(next) => {
              const joined = formatRmaQuantityModelLines(next);
              if (joined !== (rma.quantityAndModels ?? null)) {
                saveMutation.mutate({ quantityAndModels: joined });
              }
            }}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Replacement ship-to (optional)</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-2">
          <p className="md:col-span-2 text-sm text-slate-600">
            Where replacements should be delivered. Copied onto a new replacement sales order when you start an
            approval.
          </p>
          <QualityRmaSavedAddressPicker
            id="rma-ship-to-saved"
            customerFsAccountId={rma.customerFsAccountId}
            endUserFsAccountId={rma.endUserFsAccountId}
            disabled={!writesAllowed}
            onApply={(next) => {
              saveMutation.mutate({
                shipToLine1: next.line1.trim() || null,
                shipToLine2: next.line2.trim() || null,
                shipToCity: next.city.trim() || null,
                shipToState: next.state.trim() || null,
                shipToPostalCode: next.postalCode.trim() || null,
                shipToCountry: rmaAddressCountry(next),
              });
            }}
          />
          <QualityRmaAddressFields
            key={`ship-to-${rma.updatedAt}`}
            idPrefix="rma-ship-to"
            values={{
              line1: rma.shipToLine1 ?? "",
              line2: rma.shipToLine2 ?? "",
              city: rma.shipToCity ?? "",
              state: rma.shipToState ?? "",
              postalCode: rma.shipToPostalCode ?? "",
            }}
            disabled={!writesAllowed}
            onFieldCommit={(field, next) => {
              const key = SHIP_TO_FIELD_KEYS[field];
              const current = rma[key as "shipToLine1"] ?? null;
              if (next === current) return;
              saveMutation.mutate(
                next != null ? { [key]: next, shipToCountry: rma.shipToCountry ?? "US" } : { [key]: next },
              );
            }}
          />
          <div className="md:col-span-2 grid gap-4 md:grid-cols-2 md:items-end">
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-600">Replacement approval</p>
              {(detail.approvals ?? []).map((row) => (
                <div key={row.approvalTicketId} className="flex flex-wrap items-center gap-2 text-sm">
                  <Link className="text-sky-800 hover:underline" to={buildSalesOrderApprovalTicketPath(row.approvalTicketId)}>
                    {row.name}
                  </Link>
                  {row.salesOrderNumber != null && row.salesOrderNumber.trim() !== "" ? (
                    <span className="text-slate-500">{row.salesOrderNumber}</span>
                  ) : null}
                  {writesAllowed ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void removeRmaApproval(rma.id, row.approvalTicketId).then(invalidate)}
                    >
                      Remove
                    </Button>
                  ) : null}
                </div>
              ))}
              {writesAllowed ? (
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-[12rem] flex-1">
                    <Label>Link existing approval</Label>
                    <QualityApprovalPicker
                      id="rma-add-approval"
                      values={approvalDraft == null ? [] : [approvalDraft]}
                      onChange={(ids) => setApprovalDraft(ids[ids.length - 1] ?? null)}
                    />
                  </div>
                  <Button
                    disabled={approvalDraft == null}
                    onClick={() => {
                      if (approvalDraft == null) return;
                      void addRmaApproval(rma.id, approvalDraft).then(async (next) => {
                        setApprovalDraft(null);
                        await invalidate(next);
                      });
                    }}
                  >
                    Add
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={startApprovalMutation.isPending}
                    onClick={() => startApprovalMutation.mutate()}
                  >
                    {startApprovalMutation.isPending ? "Starting…" : "Start new approval"}
                  </Button>
                </div>
              ) : null}
            </div>
            <div className="space-y-2">
              {detail.salesOrders.map((row) => (
                <div key={row.salesOrderId} className="flex items-center gap-2 text-sm">
                  <SalesOrderDetailLink
                    salesOrderId={row.salesOrderId}
                    salesOrderNumber={row.salesOrderNumber ?? `Sales order ${row.salesOrderId}`}
                  />
                  {writesAllowed ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void removeRmaSalesOrder(rma.id, row.salesOrderId).then(invalidate)}
                    >
                      Remove
                    </Button>
                  ) : null}
                </div>
              ))}
              {writesAllowed ? (
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-[12rem] flex-1">
                    <Label>Add replacement sales order</Label>
                    <QualitySalesOrderPicker
                      id="rma-add-so"
                      values={salesOrderDraft == null ? [] : [salesOrderDraft]}
                      onChange={(ids) => setSalesOrderDraft(ids[ids.length - 1] ?? null)}
                    />
                  </div>
                  <Button
                    disabled={salesOrderDraft == null}
                    onClick={() => {
                      if (salesOrderDraft == null) return;
                      void addRmaSalesOrder(rma.id, salesOrderDraft).then(async (next) => {
                        setSalesOrderDraft(null);
                        await invalidate(next);
                      });
                    }}
                  >
                    Add
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle>UPS return labels</CardTitle>
          {writesAllowed ? (
            <Button className="shrink-0" onClick={() => setUpsLabelQtyOpen(true)}>
              Generate UPS return label
            </Button>
          ) : null}
        </CardHeader>
        <CardBody className="space-y-3">
          <p className="text-sm text-slate-600">
            Generates one prepaid UPS label per package, from the return ship-from address to the SGS plant. Each
            package is guessed at 5 lb and 12 by 12 by 12 inches. Fill the return ship-from address before creating
            labels.
          </p>
          {detail.upsLabels.map((label) => (
            <p key={label.id} className="font-mono text-sm">
              {label.trackingNumber}
              {label.voidedAt ? " (voided)" : ""}
            </p>
          ))}
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitleWithHelp hoverHint="Pre-intake serials are matched to a row when Receiving logs the unit. Model, firmware, and board revision fill in when the device record already has them.">
            Devices
          </CardTitleWithHelp>
          {writesAllowed ? (
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button variant="secondary" onClick={() => setPreIntakeOpen((open) => !open)}>
                Add pre-intake device
              </Button>
              <Button
                onClick={() => {
                  setIntakeEditing(null);
                  setIntakeSeed(null);
                  setIntakeModalOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Log received unit
              </Button>
            </div>
          ) : null}
        </CardHeader>
        <CardBody className="space-y-3">
          {preIntakeOpen && writesAllowed ? (
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[16rem] flex-1">
                <TextChipInput
                  ref={deviceChipRef}
                  id="rma-add-device"
                  label="Serials to match at intake"
                  chips={deviceDraftLabels}
                  onChipsChange={setDeviceDraftLabels}
                  placeholder="Type a serial and press Enter"
                />
              </div>
              <Button
                onClick={() => {
                  const labels = deviceChipRef.current?.flushDraft() ?? deviceDraftLabels;
                  if (labels.length === 0) return;
                  void (async () => {
                    try {
                      let latest = detail;
                      for (const label of labels) {
                        latest = await addRmaDevice(rma.id, label);
                      }
                      setDeviceDraftLabels([]);
                      setPreIntakeOpen(false);
                      await invalidate(latest);
                    } catch (error) {
                      showToast({
                        tone: "error",
                        message: error instanceof ApiError ? error.message : "Could not add device labels.",
                      });
                    }
                  })();
                }}
              >
                Add
              </Button>
            </div>
          ) : null}
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">Received</th>
                  <th className="px-3 py-2">Model</th>
                  <th className="px-3 py-2">Serial</th>
                  <th className="px-3 py-2">PCBA manufacturer</th>
                  <th className="px-3 py-2">AA V</th>
                  <th className="px-3 py-2">C V</th>
                  <th className="px-3 py-2">Board</th>
                  <th className="px-3 py-2">Inspection</th>
                  <th className="px-3 py-2">Resolution notes</th>
                  <th className="px-3 py-2">Logged by</th>
                  {writesAllowed ? <th className="px-3 py-2" /> : null}
                </tr>
              </thead>
              <tbody>
                {detail.intake.length === 0 && preIntakeDevices.length === 0 ? (
                  <tr>
                    <td className="px-3 py-3 text-slate-600" colSpan={writesAllowed ? 11 : 10}>
                      No devices on this RMA yet.
                    </td>
                  </tr>
                ) : (
                  <>
                    {detail.intake.map((row) => {
                      const device = devicesBySerial.get(rmaSerialKey(row.unitSerial));
                      return (
                        <tr key={`intake-${row.id}`} className="border-b border-slate-100">
                          <td className="px-3 py-2">{row.receivedAt ?? EM_DASH}</td>
                          <td className="px-3 py-2">{row.unitModel ?? device?.finishedSku ?? EM_DASH}</td>
                          <td className="px-3 py-2 font-mono text-xs">
                            {row.unitSerial != null && row.unitSerial.trim() !== "" ? (
                              <RmaSerialLookupLink
                                serial={row.unitSerial.trim()}
                                model={row.unitModel ?? device?.finishedSku}
                                firmwareVersion={device?.firmwareVersion ?? row.firmwareVersion}
                                pcbaLocationId={device?.pcbaLocationId ?? row.pcbaLocationId}
                                communicating={device?.communicating}
                              />
                            ) : (
                              EM_DASH
                            )}
                            <RmaDeviceSerialMarks device={device} />
                          </td>
                          <td className="px-3 py-2">
                            <PcbaManufacturerCell locationId={device?.pcbaLocationId ?? row.pcbaLocationId} />
                          </td>
                          <td className="px-3 py-2">{row.aaVoltage ?? EM_DASH}</td>
                          <td className="px-3 py-2">{row.cVoltage ?? EM_DASH}</td>
                          <td className="px-3 py-2">{row.boardRevision ?? EM_DASH}</td>
                          <td className="px-3 py-2">{row.visualInspection ?? EM_DASH}</td>
                          <td className="px-3 py-2">{row.resolution ?? EM_DASH}</td>
                          <td className="px-3 py-2">{row.loggedBy ?? EM_DASH}</td>
                          {writesAllowed ? (
                            <td className="px-3 py-2 text-right">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                  setIntakeSeed(null);
                                  setIntakeEditing(row);
                                  setIntakeModalOpen(true);
                                }}
                              >
                                {row.aaVoltage || row.cVoltage || row.boardRevision || row.visualInspection
                                  ? "Edit details"
                                  : "Fill in details"}
                              </Button>
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                    {preIntakeDevices.map((device) => (
                      <tr key={`device-${device.id}`} className="border-b border-slate-100">
                        <td className="px-3 py-2 text-slate-500">Pre-intake</td>
                        <td className="px-3 py-2">{device.finishedSku ?? EM_DASH}</td>
                        <td className="px-3 py-2 font-mono text-xs">
                          <RmaSerialLookupLink
                            serial={device.deviceLabelSerial}
                            model={device.finishedSku}
                            firmwareVersion={device.firmwareVersion}
                            pcbaLocationId={device.pcbaLocationId}
                            communicating={device.communicating}
                          />
                          <RmaDeviceSerialMarks device={device} />
                        </td>
                        <td className="px-3 py-2">
                          <PcbaManufacturerCell locationId={device.pcbaLocationId} />
                        </td>
                        <td className="px-3 py-2">{EM_DASH}</td>
                        <td className="px-3 py-2">{EM_DASH}</td>
                        <td className="px-3 py-2">{EM_DASH}</td>
                        <td className="px-3 py-2">{EM_DASH}</td>
                        <td className="px-3 py-2">{EM_DASH}</td>
                        <td className="px-3 py-2">{EM_DASH}</td>
                        {writesAllowed ? (
                          <td className="px-3 py-2 text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setIntakeEditing(null);
                                  setIntakeSeed({
                                    unitSerial: device.deviceLabelSerial,
                                    unitModel: device.finishedSku ?? "",
                                  });
                                  setIntakeModalOpen(true);
                                }}
                              >
                                Log intake
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => void removeRmaDevice(rma.id, device.id).then(invalidate)}
                              >
                                Remove
                              </Button>
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>RCA</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Linked RCA</p>
            {detail.rcas.length === 0 ? (
              <p className="text-sm text-slate-600">
                No RCA yet. Open one when the returned devices need root cause analysis, or link an RCA that already exists.
              </p>
            ) : (
              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2">RCA</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Opened</th>
                      <th className="px-3 py-2">Opened by</th>
                      <th className="px-3 py-2">Summary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.rcas.map((row) => {
                      const personLabel = row.openedByDisplay?.trim() || "";
                      const openedByPerson = !row.openedAutomatically && personLabel !== "";
                      return (
                        <tr key={row.id} className="border-b border-slate-100">
                          <td className="px-3 py-2">
                            <DetailLinkWithEntityPeek
                              peek={{
                                kind: "rca",
                                context: {
                                  status: row.status === "closed" ? "Closed" : "Open",
                                  openedAt: row.openedAt,
                                  openedAutomatically: row.openedAutomatically,
                                  openedByDisplay: openedByPerson ? personLabel : null,
                                  rmaNumber: rma.rmaNumber,
                                  summary: row.summary,
                                },
                              }}
                              trigger={
                                <Link
                                  className="font-medium text-sky-800 hover:underline"
                                  to={`/quality/rcas/${row.id}`}
                                >
                                  RCA {row.id}
                                </Link>
                              }
                            />
                          </td>
                          <td className="px-3 py-2">{row.status === "closed" ? "Closed" : "Open"}</td>
                          <td className="px-3 py-2">
                            {row.openedAutomatically ? "Automatically" : row.openedAt.slice(0, 10)}
                          </td>
                          <td className="px-3 py-2">
                            {openedByPerson ? (
                              <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-white py-0.5 pl-0.5 pr-2.5 text-xs font-medium text-slate-800">
                                <HubUserAvatar
                                  avatarUrl={row.openedByAvatarUrl}
                                  label={personLabel}
                                  size="sm"
                                  className="h-5 w-5 text-[10px]"
                                />
                                <span className="truncate">{personLabel}</span>
                              </span>
                            ) : (
                              EM_DASH
                            )}
                          </td>
                          <td className="px-3 py-2">{row.summary?.trim() || EM_DASH}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Linked investigations</p>
            {detail.investigations.length === 0 ? (
              <p className="text-sm text-slate-600">
                No quality investigations linked. Link an existing investigation when you need a formal writeup,
                including sending a unit out for analysis.
              </p>
            ) : (
              <ul className="space-y-1 text-sm">
                {detail.investigations.map((row) => (
                  <li key={row.investigationId}>
                    <Link className="text-sky-800 hover:underline" to={`/quality/cases/${row.qualityCaseId}`}>
                      Quality case {row.qualityCaseId}
                    </Link>
                    {EM_DASH}
                    {row.caseTitle}
                    {" · "}
                    <Link
                      className="text-sky-800 hover:underline"
                      to={`/quality/cases/${row.qualityCaseId}/investigations/${row.investigationId}/traveler`}
                    >
                      Traveler
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {writesAllowed ? (
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={openRcaPending}
                onClick={() => {
                  setOpenRcaPending(true);
                  void openRmaRca(rma.id, null)
                    .then(async (next) => {
                      await invalidate(next);
                      showToast({ tone: "success", message: `Opened RCA ${next.openedRcaId}.` });
                      void navigate(`/quality/rcas/${next.openedRcaId}`);
                    })
                    .catch((error: unknown) => {
                      showToast({ tone: "error", message: error instanceof Error ? error.message : "Could not open the RCA." });
                    })
                    .finally(() => setOpenRcaPending(false));
                }}
              >
                {openRcaPending ? "Opening" : detail.rcas.length === 0 ? "Open RCA" : "Open another RCA"}
              </Button>
              <Button variant="secondary" onClick={() => setLinkRcaOpen(true)}>
                {detail.rcas.length === 0 ? "Link existing RCA" : "Link additional RCA"}
              </Button>
              <Button variant="secondary" onClick={() => setLinkInvestigationOpen(true)}>
                Link quality investigation
              </Button>
            </div>
          ) : null}
        </CardBody>
      </Card>
      <QualityRmaUpsLabelQtyModal
        open={upsLabelQtyOpen}
        onClose={() => setUpsLabelQtyOpen(false)}
        onGenerate={async (quantity) => {
          const result = await createRmaUpsReturnLabel(rma.id, { quantity });
          await invalidate(result.detail);
          const count = result.labels.length;
          showToast({
            tone: "success",
            message:
              count > 1
                ? `Created ${count} UPS labels: ${result.tracking || "created"}.`
                : `UPS tracking ${result.tracking || "created"}.`,
          });
          result.labels.forEach((label, index) => {
            const suffix = label.tracking_no?.trim() || String(index + 1);
            const downloads: Array<{ data: string; filename: string }> = [];
            if (label.letter_pdf_base64) {
              downloads.push({ data: label.letter_pdf_base64, filename: `${rma.rmaNumber}-${suffix}-ups-letter.pdf` });
            }
            if (label.label_4x6_pdf_base64) {
              downloads.push({ data: label.label_4x6_pdf_base64, filename: `${rma.rmaNumber}-${suffix}-ups-4x6.pdf` });
            }
            if (downloads.length === 0 && label.img_base64) {
              downloads.push({ data: label.img_base64, filename: `${rma.rmaNumber}-${suffix}-ups.png` });
            }
            for (const file of downloads) {
              const bytes = Uint8Array.from(atob(file.data), (char) => char.charCodeAt(0));
              const blob = new Blob([bytes], {
                type: file.filename.endsWith(".pdf") ? "application/pdf" : "image/png",
              });
              const url = URL.createObjectURL(blob);
              const anchor = document.createElement("a");
              anchor.href = url;
              anchor.download = file.filename;
              document.body.appendChild(anchor);
              anchor.click();
              anchor.remove();
              URL.revokeObjectURL(url);
            }
          });
        }}
      />
      <QualityRmaLinkIdModal
        open={linkRcaOpen}
        title="Link existing RCA"
        idLabel="RCA id"
        submitLabel="Link RCA"
        onClose={() => setLinkRcaOpen(false)}
        onLink={async (rcaId) => {
          const next = await linkRmaRca(rma.id, rcaId);
          await invalidate(next);
        }}
      />
      <QualityRmaLinkIdModal
        open={linkInvestigationOpen}
        title="Link quality investigation"
        idLabel="Investigation id"
        submitLabel="Link investigation"
        onClose={() => setLinkInvestigationOpen(false)}
        onLink={async (investigationId) => {
          const next = await linkRmaInvestigation(rma.id, investigationId);
          await invalidate(next);
        }}
      />

      <QualityRmaIntakeModal
        open={intakeModalOpen}
        rmaId={rma.id}
        intake={intakeEditing}
        seedSerial={intakeSeed?.unitSerial}
        seedModel={intakeSeed?.unitModel}
        onClose={() => {
          setIntakeModalOpen(false);
          setIntakeEditing(null);
          setIntakeSeed(null);
        }}
        onLogged={(next) => {
          void invalidate(next);
          showToast({
            tone: "success",
            message: intakeEditing != null ? "Intake details saved." : "Received unit logged.",
          });
        }}
      />
    </div>
  );
}
